import React, { ChangeEvent, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Box, Grid, Table, TableBody, TablePagination, ThemeProvider } from '@mui/material';
import { GlobalState } from 'reducers/stateTypes';
import CustomDateRangePicker from 'components/Common/CustomDateRangePicker';
import { isNotNullOrUndefined } from 'utils/utils';
import moment from 'moment';
import { JBTheme } from '@bjb-gcrm/gcrm-frontend-components';
import { SkeletonTableRow } from 'components/Common/SkeletonTableRow';
import { stableSort } from 'utils/dataTableUtils';
import { TextFilter } from 'components/Common/TextFilter';
import { Employee } from 'types/Partner';
import { useFetchBrVersions } from 'hooks/queries/useFetchBrVersions';
import { BrVersionDto } from 'views/RevisionHistory/Br/BrRevisionDtos';
import { BrRevisionHistoryRow } from 'views/RevisionHistory/Br/BrRevisionHistoryRow';
import { addHours } from 'date-fns';

function filterDateRange(dateRange: [Date | null, Date | null], sortedRevisionHistoryItems: any[]) {
  const dateFrom = moment.isDate(dateRange[0]) ? new Date(dateRange[0]).toISOString() : null;
  //Add 24 hours to include the full 'to' date
  const dateTo = moment.isDate(dateRange[1]) ? addHours(new Date(dateRange[1]), 24).toISOString() : null;

  return isNotNullOrUndefined(dateTo) && isNotNullOrUndefined(dateFrom)
    ? sortedRevisionHistoryItems?.filter(
        (item: BrVersionDto) => item.modifiedTimestamp >= dateFrom && item.modifiedTimestamp <= dateTo,
      )
    : sortedRevisionHistoryItems;
}

function filterSearchWord(searchWord: string, dateFiltered: BrVersionDto[], thRm: Employee[]) {
  if (searchWord === '') return dateFiltered;
  const lowercaseSearchWord = searchWord.toLowerCase();
  return dateFiltered.filter((row) => {
    const employee = thRm.find((employee) => employee.userId === row.modifiedBy);
    const searchRow = row.submitReason + ' ' + employee?.fullName;
    return searchRow.toLowerCase().includes(lowercaseSearchWord);
  });
}

const BrRevisionHistoryTable = ({ techId }: { techId: string }) => {
  const { t } = useTranslation();
  const { data: revisionHistoryItems = [], isFetching } = useFetchBrVersions(techId);
  const thRm: Employee[] = useSelector((state: GlobalState) => state.partnerForm.thRm);
  const [dateRange, setDateRange] = useState<[null | Date, null | Date]>([null, null]);
  const [searchWord, setSearchWord] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const sortedRevisionHistoryItems = useMemo(() => {
    return stableSort(revisionHistoryItems, (a, b) => b.version - a.version);
  }, [revisionHistoryItems]);

  const filteredItems = useMemo(() => {
    const dateFiltered = filterDateRange(dateRange, sortedRevisionHistoryItems);
    return filterSearchWord(searchWord, dateFiltered, thRm);
  }, [dateRange, sortedRevisionHistoryItems, searchWord, thRm]);

  const pagedItems = useMemo(() => {
    return filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  if (isFetching) {
    return (
      <Box sx={{ width: '100%' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableBody>
            <SkeletonTableRow rowCount={5} cellCount={1} id="revision-table-skeleton" />
          </TableBody>
        </Table>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={JBTheme}>
      <Grid container direction="row" rowGap={1} gap={2}>
        <Grid item>
          <CustomDateRangePicker
            setSelectedDate={setDateRange}
            selectedDate={dateRange}
            disabled={false}
            useFormik={false}
            label={t('revisionHistory.dateRange')}
          />
        </Grid>
        <Grid item xs={4}>
          <TextFilter
            filterName={t('revisionHistory.search.label')}
            placeholderText={t('revisionHistory.search.placeholder')}
            id="revision-history-filter-input"
            dispatchAction={(val: string) => {
              setSearchWord(val);
            }}
          />
        </Grid>
        <Grid item xs={12} container rowGap={1}>
          {pagedItems.map((history) => (
            <Grid item xs={12} key={history.version}>
              <BrRevisionHistoryRow
                techId={techId}
                brVersionDtos={sortedRevisionHistoryItems}
                version={history.version}
                employees={thRm}
              />
            </Grid>
          ))}
        </Grid>
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 20, 50]}
          count={sortedRevisionHistoryItems?.length}
          rowsPerPage={rowsPerPage}
          slotProps={{
            actions: {
              nextButton: {
                id: 'next-page-button',
              },
              previousButton: {
                id: 'previous-page-button',
              },
            },
          }}
          page={page}
          style={{ marginLeft: 'auto' }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Entries per page"
        />
      </Grid>
    </ThemeProvider>
  );
};

export default BrRevisionHistoryTable;
